import { select } from '@redux-saga/core/effects';
import { makeSelectPaintColor } from '../../../PaintPanel/selectors';
import { Door } from '../../../../lib/modelBuilder/pieces/Door';
import { Pane } from '../../../../lib/modelBuilder/pieces/Pane';
import { Drawer } from '../../../../lib/modelBuilder/pieces/Drawer';
import { Plate } from '../../../../lib/modelBuilder/pieces/Plate';
import { Cabinet } from '../../../../lib/modelBuilder/entities/Cabinet';
import { getMaterialColor } from '../../../../lib/modelBuilder/utils/colorHelper';
import {
  BLACK_COLOR,
  DoorHandleType,
  DrawerHandleType,
  Material,
  NATURAL_COLOR,
  PaneMaterial,
  ShelfComposition,
  WHITE_COLOR,
} from '../../../../lib/modelBuilder/constants';
import { IntermediateShelf } from '../../../../lib/modelBuilder/pieces/IntermediateShelf';
import {
  makeSelectPaintColorHandles,
  makeSelectPaintMaterialHandles,
  makeSelectPaintTypeHandles,
} from '../../../HandlesPanel/selectors';
import { Color } from '../../../../lib/modelBuilder/entities/Color';
import { makeSelectPaintHandlesMode } from '../../../ModeBar/selectors';
import { HandlesMode } from '../../../ModeBar/reducer';
import { updateMaterialWhenEco } from './handles';
import { Furniture } from '../../../../lib/modelBuilder/entities/Furniture';
import { makeSelectFurniture } from '../../selectors/furniture';
import { put } from 'redux-saga/effects';
import { addRecentModified } from '../../../IconsBarCanvas/RemoveShelfButton/actions';
import { UpperShelf } from '../../../../lib/modelBuilder/pieces/UpperShelf';

export function* applyPaintColorSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
): any {
  const {
    customPiece,
    cabinetId,
    shelfLevel,
    rackLevel,
    interShelfLevel,
  } = payload;
  const color = yield select(makeSelectPaintColor());

  if (!color) {
    throw new Error(`Color should be selected before in paint mode`);
  }

  const furniture: Furniture = yield select(makeSelectFurniture());
  const cabinetEntry = furniture.cabinets.find(e => e.cabinet.id === cabinetId);
  if (!cabinetEntry) {
    throw new Error(`Cabinet not found with id ${cabinetId}`);
  }
  const newCabinetOrigin = cabinetEntry.cabinet;
  const cabinet = newCabinetOrigin.clone();

  if (customPiece.base instanceof UpperShelf) {
    cabinet.addOrUpdateUpperShelf(undefined, undefined, color);
  }

  if (customPiece.base instanceof Door) {
    if (shelfLevel === undefined) {
      throw new Error(`Door need a shelf to apply color`);
    }
    cabinet
      .shelf(shelfLevel)
      .updateDoor(
        customPiece.position,
        undefined,
        undefined,
        getMaterialColor(color),
        undefined,
        color,
      );
  } else if (customPiece.base instanceof Pane) {
    if (shelfLevel === undefined) {
      throw new Error(`Pane need a shelf to apply color`);
    }
    if (rackLevel === undefined) {
      throw new Error(`Pane need a rack to apply color`);
    }
    cabinet
      .shelf(shelfLevel)
      .rack(rackLevel)
      .updatePane(
        customPiece.position,
        undefined,
        undefined,
        getMaterialColor(color),
        color,
      );
    cabinet.shelf(shelfLevel).updateSupportIS();
  } else if (customPiece.base instanceof Drawer) {
    if (shelfLevel === undefined) {
      throw new Error(`Drawer need a shelf to apply color`);
    }
    cabinet
      .shelf(shelfLevel)
      .updateDrawer(
        customPiece.position,
        undefined,
        undefined,
        undefined,
        color,
      );
  } else if (customPiece.base instanceof Plate) {
    const materialColor = getMaterialColor(color);
    let glass = false;
    if (
      materialColor === PaneMaterial.MDF ||
      materialColor === PaneMaterial.Frost ||
      materialColor === PaneMaterial.FrostOpaque
    ) {
      glass = true;
    }
    if (
      color.reference === WHITE_COLOR ||
      color.reference === BLACK_COLOR ||
      color.reference === NATURAL_COLOR
    ) {
      glass = false;
    }
    if (shelfLevel === undefined && rackLevel === undefined) {
      cabinet.top.updatePlate(
        undefined,
        undefined,
        materialColor,
        color,
        glass,
      );
    } else {
      cabinet
        .shelf(shelfLevel)
        .rack(rackLevel)
        .updatePlate(undefined, undefined, materialColor, color, glass);
    }
  } else if (customPiece.base instanceof IntermediateShelf) {
    cabinet
      .shelf(shelfLevel)
      .getIntermediateShelf(interShelfLevel)
      ?.updateIntermediateShelf(undefined, undefined, color);
    cabinet.shelf(shelfLevel).updateSupportIS();
  }

  return {
    ignore: false,
    postSuccess: null,
    newCabinetOrigin: newCabinetOrigin,
    newCabinetNext: cabinet,
  };
}

export function* applyDeleteSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
): any {
  const {
    customPiece,
    cabinetId,
    shelfLevel,
    rackLevel,
    interShelfLevel,
  } = payload;
  const furniture: Furniture = yield select(makeSelectFurniture());
  const cabinetEntry = furniture.cabinets.find(e => e.cabinet.id === cabinetId);
  if (!cabinetEntry) {
    throw new Error(`Cabinet not found with id ${cabinetId}`);
  }
  const newCabinetOrigin = cabinetEntry.cabinet;
  const cabinet = newCabinetOrigin.clone();

  if (customPiece.base instanceof Door) {
    cabinet.shelf(shelfLevel).removeDoor(customPiece.position);
  } else if (customPiece.base instanceof Pane) {
    cabinet
      .shelf(shelfLevel)
      .rack(rackLevel)
      .removePane(customPiece.position);
  } else if (customPiece.base instanceof Drawer) {
    cabinet.shelf(shelfLevel).composition = ShelfComposition.Open;
  } else if (customPiece.base instanceof Plate) {
    if (shelfLevel === undefined && rackLevel === undefined) {
      cabinet.top.removePlate();
    } else {
      cabinet
        .shelf(shelfLevel)
        .rack(rackLevel)
        .removePlate();
    }
  } else if (customPiece.base instanceof IntermediateShelf) {
    cabinet.shelf(shelfLevel).removeIntermediateShelf([interShelfLevel]);
  } else if (customPiece.base instanceof UpperShelf) {
    cabinet.removeUpperShelf();
  }
  if (shelfLevel) yield put(addRecentModified(cabinet.shelf(shelfLevel)));

  return {
    ignore: false,
    postSuccess: null,
    newCabinetOrigin: newCabinetOrigin,
    newCabinetNext: cabinet,
  };
}

export function* applyColorHandleAction(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
): any {
  const { customPiece, cabinetId, shelfLevel } = payload;

  const color: Color | undefined = yield select(makeSelectPaintColorHandles());
  const type = yield select(makeSelectPaintTypeHandles());
  const material: Material | undefined = yield select(
    makeSelectPaintMaterialHandles(),
  );

  const handleMode = yield select(makeSelectPaintHandlesMode());

  const furniture: Furniture = yield select(makeSelectFurniture());
  const cabinetEntry = furniture.cabinets.find(e => e.cabinet.id === cabinetId);
  if (!cabinetEntry) {
    throw new Error(`Cabinet not found with id ${cabinetId}`);
  }
  const newCabinetOrigin = cabinetEntry.cabinet;
  const cabinet = newCabinetOrigin.clone();

  if (type) {
    if (handleMode !== HandlesMode.drawers) {
      if (type !== DoorHandleType.Slat) {
        cabinet
          .shelf(shelfLevel)
          .addDoorHandle(customPiece.position, type, color);
      } else {
        if (material) {
          cabinet
            .shelf(shelfLevel)
            .setLargeDoorSlat(customPiece.position, material);
        } else {
          return {
            ignore: true,
            postSuccess: null,
          };
        }
      }
    } else {
      if (type === DrawerHandleType.Eco || type === DrawerHandleType.EcoPlus) {
        cabinet
          .shelf(shelfLevel)
          .updateDrawer(
            customPiece.position,
            undefined,
            undefined,
            undefined,
            color,
          );
      } else {
        updateMaterialWhenEco(cabinet.shelf(shelfLevel), customPiece.position);
        cabinet
          .shelf(shelfLevel)
          .addDrawerHandle(customPiece.position, type, color);
      }
    }
  } else {
    return {
      ignore: true,
      postSuccess: null,
    };
  }

  return {
    ignore: false,
    postSuccess: null,
    newCabinetOrigin: newCabinetOrigin,
    newCabinetNext: cabinet,
  };
}
